import { ProjectStorage } from './storage/ProjectStorage';
import { ProjectManager } from './ui/ProjectManager';

import { UnifiedModeController } from './modes/unified/UnifiedModeController';
import type { UnifiedCanvas } from './modes/unified/UnifiedCanvas';
import { UnifiedToolbar } from './ui/UnifiedToolbar';
import type { LineStyle } from './ui/UnifiedToolbar';
import type { DiagramTool, ArrowHead, TextStyle } from './modes/diagram/state/types';
import type { Connector } from './modes/diagram/shapes/Connector';

import { SessionController } from './app/SessionController';
import { ProjectController } from './app/ProjectController';
import { loadLastSessionFromStorage } from './app/sessionLoad';
import { ThemeController } from './app/ThemeController';
import type { AppMode } from './types';
import type { ToolType } from './types';
import { ExportController } from './app/ExportController';
import { AppContextBuilder } from './app/AppContextBuilder';
import { STORAGE_KEYS } from './config/storageKeys';
import { UnifiedStore } from './modes/unified/UnifiedStore';
import { buildViewportToolbarCallbacks, confirmClear } from './app/ViewportToolbarHelper';
import {
  APP_SHORTCUTS,
  handleShortcutActions,
  isMacPlatform,
} from './input/shortcuts';
import './styles/base.css';
import './styles/toolbar.css';
import './styles/draw.css';
import './styles/diagram.css';
import './styles/modals.css';

export class DrawlingsApp {
  private modeController: UnifiedModeController | null = null;

  private storage: ProjectStorage | null = null;

  private projectManager: ProjectManager | null = null;

  private sessionController: SessionController | null = null;

  private projectController: ProjectController | null = null;

  private themeController: ThemeController | null = null;

  private exportController: ExportController | null = null;

  private currentProjectId: string | null = null;

  private currentProjectName = 'Untitled';

  private mode: AppMode = 'draw';

  private canvasEl: HTMLCanvasElement | null = null;

  private toolbarEl: HTMLElement | null = null;

  private readonly unifiedStore = new UnifiedStore();

  private unifiedState: string | null = null;

  // Active toolbar reference
  private unifiedToolbar: UnifiedToolbar | null = null;

  private static readonly LAST_SESSION_KEY = STORAGE_KEYS.lastSession;

  // State accessor methods
  private getCanvasElAccessor(): HTMLCanvasElement | null { return this.canvasEl; }

  private getToolbarElAccessor(): HTMLElement | null { return this.toolbarEl; }

  private getStorageAccessor(): ProjectStorage | null { return this.storage; }

  private getProjectManagerAccessor(): ProjectManager | null { return this.projectManager; }

  private getModeAccessor(): AppMode { return this.mode; }

  private setModeAccessor(mode: AppMode): void { this.mode = mode; }

  private getCurrentProjectIdAccessor(): string | null { return this.currentProjectId; }

  private setCurrentProjectIdAccessor(id: string | null): void { this.currentProjectId = id; }

  private getCurrentProjectNameAccessor(): string { return this.currentProjectName; }

  private setCurrentProjectNameAccessor(name: string): void { this.currentProjectName = name; }

  private getUnifiedStateAccessor(): string | null { return this.unifiedState; }

  private setUnifiedStateAccessor(state: string | null): void { this.unifiedState = state; }

  private getThemeControllerAccessor(): ThemeController | null { return this.themeController; }

  private getExportControllerAccessor(): ExportController | null { return this.exportController; }

  private getUnifiedCanvasAccessor(): UnifiedCanvas | null {
    return this.modeController?.getCanvas() ?? null;
  }

  private getUnifiedToolbarAccessor(): UnifiedToolbar | null { return this.unifiedToolbar; }

  private buildStateAccessors(): import('./app/AppContextBuilder').AppStateAccessors {
    return {
      getCanvasEl: this.getCanvasElAccessor.bind(this),
      getToolbarEl: this.getToolbarElAccessor.bind(this),
      getStorage: this.getStorageAccessor.bind(this),
      getProjectManager: this.getProjectManagerAccessor.bind(this),
      getMode: this.getModeAccessor.bind(this),
      setMode: this.setModeAccessor.bind(this),
      getCurrentProjectId: this.getCurrentProjectIdAccessor.bind(this),
      setCurrentProjectId: this.setCurrentProjectIdAccessor.bind(this),
      getCurrentProjectName: this.getCurrentProjectNameAccessor.bind(this),
      setCurrentProjectName: this.setCurrentProjectNameAccessor.bind(this),
      getUnifiedState: this.getUnifiedStateAccessor.bind(this),
      setUnifiedState: this.setUnifiedStateAccessor.bind(this),
      getThemeController: this.getThemeControllerAccessor.bind(this),
      getExportController: this.getExportControllerAccessor.bind(this),
      getUnifiedStore: (): UnifiedStore => this.unifiedStore,
      getUnifiedCanvas: this.getUnifiedCanvasAccessor.bind(this),
      getUnifiedToolbar: this.getUnifiedToolbarAccessor.bind(this),
      onScheduleAutoSave: this.scheduleAutoSave.bind(this),
      onSwitchMode: this.switchMode.bind(this),
      onLoadProject: this.handleLoadProject.bind(this),
      onRefreshProjectList: this.refreshProjectListInternal.bind(this),
      onLoadCurrentModeState: this.loadCurrentModeState.bind(this),
      onSetupToolbar: this.setupToolbar.bind(this),
    };
  }

  async init(): Promise<void> {
    this.storage = new ProjectStorage();
    await this.storage.init();

    this.canvasEl = document.getElementById('drawing-canvas') as HTMLCanvasElement | null;
    this.toolbarEl = document.getElementById('toolbar');
    const projectPanelEl = document.getElementById('project-panel');

    if (!this.canvasEl || !this.toolbarEl || !projectPanelEl) {
      throw new Error('Required DOM elements not found');
    }

    // Create context builder with all app state accessors
    const contextBuilder = new AppContextBuilder(this.buildStateAccessors());

    // Create controllers using the context builder
    this.themeController = new ThemeController(contextBuilder.buildThemeContext());
    this.themeController.createModeSwitcher();

    this.exportController = new ExportController(contextBuilder.buildExportContext());

    this.modeController = new UnifiedModeController(contextBuilder.buildUnifiedModeContext());

    // Initialize (no mode param — always unified)
    this.modeController.init();

    // Create project manager with callbacks
    this.projectManager = new ProjectManager(projectPanelEl, {
      onNew: this.onNewProject.bind(this),
      onLoad: this.onLoadProject.bind(this),
      onDelete: this.onDeleteProject.bind(this),
      onRename: this.onRenameProject.bind(this),
    });

    // Create session and project controllers
    this.sessionController = new SessionController(
      contextBuilder.buildSessionContext(),
      { autoSaveDelay: 500, lastSessionKey: DrawlingsApp.LAST_SESSION_KEY }
    );
    this.projectController = new ProjectController(
      contextBuilder.buildProjectContext(),
      this.sessionController
    );

    this.setupKeyboardShortcuts();
    await this.refreshProjectList();

    // Auto-load last session
    await this.loadLastSession();
  }

  private async loadLastSession(): Promise<void> {
    if (this.sessionController) {
      await this.sessionController.loadLastSession();
      return;
    }

    await loadLastSessionFromStorage({
      storageKey: DrawlingsApp.LAST_SESSION_KEY,
      getStorage: this.getStorageAccessor.bind(this),
      getMode: this.getModeAccessor.bind(this),
      loadProject: this.handleLoadProject.bind(this),
      switchMode: this.switchMode.bind(this),
    });
  }

  private saveLastSession(): void {
    if (this.sessionController) {
      this.sessionController.saveLastSession();
      return;
    }

    if (!this.currentProjectId) return;
    const sessionData = JSON.stringify({
      projectId: this.currentProjectId,
      mode: this.mode,
    });
    localStorage.setItem(DrawlingsApp.LAST_SESSION_KEY, sessionData);
  }

  /**
   * switchMode is kept for backward compat with session/project loading.
   * In the unified world it's a no-op for the UI but updates the internal mode field.
   */
  private switchMode(mode: AppMode, _preserveCurrentState = true): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.saveLastSession();
  }

  /**
   * Sets up the unified toolbar. Called by UnifiedModeController on init.
   * The mode parameter is ignored — we always create a UnifiedToolbar.
   */
  private setupToolbar(canvas: UnifiedCanvas, _mode: AppMode): void {
    // Destroy previous toolbar
    this.unifiedToolbar?.destroy();
    this.unifiedToolbar = null;

    const toolbarEl = this.toolbarEl;
    if (!toolbarEl) return;

    const commonCallbacks = buildViewportToolbarCallbacks({
      getCanvas: (): UnifiedCanvas | null => canvas,
      getMode: (): AppMode => this.mode,
      getExportController: (): ExportController | null => this.exportController,
    });

    this.setupUnifiedToolbar(toolbarEl, canvas, commonCallbacks);
  }

  private setupUnifiedToolbar(
    toolbarEl: HTMLElement,
    canvas: UnifiedCanvas,
    commonCallbacks: ReturnType<typeof buildViewportToolbarCallbacks>
  ): void {
    const handleToolChange = (tool: ToolType | DiagramTool | string): void => {
      canvas.setTool(tool as ToolType);
      const textStyle = canvas.getSelectedTextStyle();
      this.unifiedToolbar?.updateTextFormatVisibility(tool === 'text' || textStyle !== null, textStyle);
    };
    const handleShapeSubtypeChange = (subtype: 'draw' | 'diagram'): void => {
      canvas.setShapeCreationMode(subtype);
    };
    const handleColorChange = (color: string): void => {
      canvas.setColor(color);
    };
    const handleSizeChange = (size: number): void => {
      canvas.setBrushSize(size);
    };
    const handleStyleChange = (style: { fill?: string; stroke?: string; strokeWidth?: number }): void => {
      if (style.fill !== undefined || style.stroke !== undefined || style.strokeWidth !== undefined) {
        canvas.setStyle(style);
      }
    };
    const handleTextStyleChange = (style: Partial<TextStyle>): void => {
      canvas.setTextStyle(style);
    };
    const handleLineFormatChange = (type: 'normal'): void => {
      canvas.applyLineFormat(type);
    };
    const handleTextListAction = (kind: 'ordered' | 'unordered'): void => {
      canvas.applyTextListAction(kind);
    };
    const handleConnectorStyleChange = (style: {
      startArrow?: ArrowHead;
      endArrow?: ArrowHead;
      lineStyle?: LineStyle;
      strokeWidth?: number;
    }): void => {
      canvas.updateSelectedConnectorStyle(style);
    };
    const handleToggleArc = (): void => {
      canvas.toggleSelectedConnectorArc();
    };
    const handleUndo = (): void => {
      canvas.undo();
      this.unifiedToolbar?.updateHistoryButtons(canvas.canUndo(), canvas.canRedo());
    };
    const handleRedo = (): void => {
      canvas.redo();
      this.unifiedToolbar?.updateHistoryButtons(canvas.canUndo(), canvas.canRedo());
    };
    const handleClear = (): void => {
      confirmClear((): void => { canvas.clear(); });
    };
    const handleDeleteSelected = (): void => {
      canvas.deleteSelected();
    };
    const handleStrokeEraseToggle = (enabled: boolean): void => {
      canvas.setStrokeEraseMode(enabled);
    };
    const handleFitToScreen = (): void => {
      canvas.fitToScreen();
    };

    this.unifiedToolbar = new UnifiedToolbar(toolbarEl, {
      ...commonCallbacks,
      onToolChange: handleToolChange,
      onShapeSubtypeChange: handleShapeSubtypeChange,
      onColorChange: handleColorChange,
      onSizeChange: handleSizeChange,
      onStyleChange: handleStyleChange,
      onTextStyleChange: handleTextStyleChange,
      onLineFormatChange: handleLineFormatChange,
      onTextListAction: handleTextListAction,
      onConnectorStyleChange: handleConnectorStyleChange,
      onToggleArc: handleToggleArc,
      onUndo: handleUndo,
      onRedo: handleRedo,
      onClear: handleClear,
      onDeleteSelected: handleDeleteSelected,
      onStrokeEraseModeToggle: handleStrokeEraseToggle,
      onFitToScreen: handleFitToScreen,
    });

    // Wire canvas → toolbar sync
    canvas.setOnSelectionChange((hasSelection: boolean, textStyle: TextStyle | null): void => {
      const isTextTool = canvas.getTool() === 'text';
      this.unifiedToolbar?.updateTextFormatVisibility(isTextTool || textStyle !== null, textStyle);
      this.unifiedToolbar?.updateDeleteButton(hasSelection);
      if (hasSelection) {
        const strokeWidth = canvas.getSelectedStrokeWidth();
        if (strokeWidth !== null) {
          this.unifiedToolbar?.updateStrokeWidth(strokeWidth);
        }
      }
    });
    canvas.setOnBrushSizeChange((size: number): void => {
      this.unifiedToolbar?.setBrushSize(size);
    });
    canvas.setOnHistoryChange((canUndo: boolean, canRedo: boolean): void => {
      this.unifiedToolbar?.updateHistoryButtons(canUndo, canRedo);
    });
    canvas.setOnViewChange((scale: number): void => {
      this.unifiedToolbar?.updateZoom(scale);
      this.themeController?.updateZoom(scale);
    });
    canvas.setOnConnectorSelection((connector: Connector | null): void => {
      if (connector) {
        this.unifiedToolbar?.showConnectorOptions(
          connector.style.startArrow,
          connector.style.endArrow,
          connector.style.lineStyle as LineStyle,
          connector.arcPoint !== null
        );
        this.unifiedToolbar?.updateStrokeWidth(connector.style.strokeWidth);
      } else {
        this.unifiedToolbar?.hideConnectorOptions();
      }
    });
    // Apply theme defaults
    const themeController = this.themeController;
    const inverted = themeController?.getInvertedFromTheme() ?? false;
    const defaultColor = themeController?.getThemeDrawingColor(inverted) ?? '#000000';
    this.unifiedToolbar.setColor(canvas.getColor() || defaultColor);
    this.unifiedToolbar.setInvertMode(inverted);
    this.unifiedToolbar.setActiveTool(canvas.getTool() as ToolType);
    this.unifiedToolbar.setBrushSize(canvas.getBrushSize());
    this.unifiedToolbar.setGridEnabled(canvas.isGridEnabled());
    this.unifiedToolbar.setSnapEnabled(canvas.isSnapEnabled());
    // Sync header view controls with canvas state
    this.themeController?.setGridEnabled(canvas.isGridEnabled());
    this.themeController?.setSnapEnabled(canvas.isSnapEnabled());
    this.themeController?.setInvertEnabled(inverted);
    this.themeController?.updateZoom(canvas.getScale());
    const initTextStyle = canvas.getSelectedTextStyle();
    this.unifiedToolbar.updateTextFormatVisibility(
      canvas.getTool() === 'text' || initTextStyle !== null,
      initTextStyle
    );
    this.unifiedToolbar.updateHistoryButtons(canvas.canUndo(), canvas.canRedo());
    this.unifiedToolbar.updateZoom(canvas.getScale());

    // Sync shape creation mode from canvas
    this.unifiedToolbar.setShapeSubtype(canvas.getShapeCreationMode());
  }

  private async handleNewProject(): Promise<void> {
    await this.projectController?.handleNewProject();
  }

  private onNewProject(): void | Promise<void> {
    return this.handleNewProject();
  }

  private onLoadProject(id: string): void {
    void this.handleLoadProject(id);
  }

  private onDeleteProject(id: string): void {
    void this.handleDeleteProject(id);
  }

  private onRenameProject(id: string, newName: string): void {
    void this.handleRenameProject(id, newName);
  }

  private scheduleAutoSave(): void {
    const handleAutoSave = (): void => {
      void this.performAutoSave();
    };
    this.sessionController?.scheduleAutoSave(handleAutoSave);
  }

  private async performAutoSave(forceRefreshList = false, forceThumbnailUpdate = false): Promise<void> {
    await this.sessionController?.performAutoSave(forceRefreshList, forceThumbnailUpdate);
  }

  private loadCurrentModeState(): void {
    const currentThemeInverted = this.themeController?.getInvertedFromTheme() ?? false;
    const state = this.unifiedState;

    if (this.modeController) {
      this.modeController.restoreState(currentThemeInverted, state, true);
    }
  }

  private async handleLoadProject(id: string): Promise<void> {
    await this.projectController?.handleLoadProject(id);
  }

  private async handleDeleteProject(id: string): Promise<void> {
    await this.projectController?.handleDeleteProject(id);
  }

  private async handleRenameProject(id: string, newName: string): Promise<void> {
    await this.projectController?.handleRenameProject(id, newName);
  }

  private async refreshProjectList(): Promise<void> {
    if (this.projectController) {
      await this.projectController.refreshProjectList();
      return;
    }
    await this.refreshProjectListInternal();
  }

  private async refreshProjectListInternal(): Promise<void> {
    if (!this.storage || !this.projectManager) return;

    const projects = await this.storage.listProjects();
    this.projectManager.updateProjects(projects);
  }

  private setupKeyboardShortcuts(): void {
    const handleAppKeydown = (e: KeyboardEvent): void => {
      const isMac = isMacPlatform();

      const handleSaveShortcut = (): void => {
        void this.performAutoSave();
      };

      handleShortcutActions(
        e,
        [
          {
            shortcut: APP_SHORTCUTS.save,
            action: handleSaveShortcut,
            preventDefault: true,
          },
        ],
        isMac
      );
    };
    document.addEventListener('keydown', handleAppKeydown);
  }

}

if (typeof window !== 'undefined' && !(import.meta as { vitest?: unknown }).vitest) {
  const app = new DrawlingsApp();
  app.init().catch((err: unknown): void => {
    const message = err instanceof Error ? err.message : 'Unknown error';
    const errorContainer = document.createElement('div');
    errorContainer.className = 'error';
    errorContainer.textContent = `Failed to initialize: ${message}`;
    document.body.replaceChildren(errorContainer);
  });
}
